/*
  Zaktualizowano geometrię:
  1. Rozszerzono getSnapPoint o przyciąganie do ścian (wall snapping).
  2. Jeśli kursor jest blisko ściany, a nie ma w pobliżu narożnika, przyciągnie go do linii ściany.
  3. Poprawia to precyzję rysowania pomieszczeń i segmentów ścian.
*/
import { v4 as uuidv4 } from 'uuid';

/**
 * Oblicza odległość między dwoma punktami
 */
export const distance = (p1, p2) => {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
};

/**
 * Funkcja przyciągania do siatki (Grid Snapping)
 */
export const snapToGrid = (val, gridSize) => {
  return Math.round(val / gridSize) * gridSize;
};

/**
 * Znajduje najbliższy punkt (snap point) na podstawie pozycji myszy
 * Sprawdza istniejące wierzchołki, ściany i siatkę
 */
export const getSnapPoint = (pos, corners, threshold, gridSize, walls = null) => {
  // 1. Sprawdź przyciąganie do istniejących wierzchołków (najwyższy priorytet)
  let closestCorner = null;
  let minDist = Infinity;

  Object.values(corners).forEach(corner => {
    const d = distance(pos, corner);
    if (d < threshold && d < minDist) {
      minDist = d;
      closestCorner = corner;
    }
  });

  if (closestCorner) {
    return { x: closestCorner.x, y: closestCorner.y, snappedTo: 'corner', id: closestCorner.id };
  }

  // 2. Sprawdź przyciąganie do linii ścian (jeśli przekazano ściany)
  if (walls) {
    const wallSnap = getClosestWall(pos, walls, corners, threshold);
    if (wallSnap) {
      return { 
        x: wallSnap.snapInfo.point.x, 
        y: wallSnap.snapInfo.point.y, 
        snappedTo: 'wall', 
        id: wallSnap.wall.id 
      };
    }
  }

  // 3. Jeśli nie przyciągnięto do niczego innego, przyciągnij do siatki
  return { x: snapToGrid(pos.x, gridSize), y: snapToGrid(pos.y, gridSize), snappedTo: 'grid' };
};

/**
 * Oblicza odległość punktu od linii ściany
 */
export const pointToLineDistance = (point, lineStart, lineEnd) => {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) param = dot / lenSq;

  let xx, yy;

  if (param < 0) {
    xx = lineStart.x;
    yy = lineStart.y;
  } else if (param > 1) {
    xx = lineEnd.x;
    yy = lineEnd.y;
  } else {
    xx = lineStart.x + param * C;
    yy = lineStart.y + param * D;
  }

  const distance = Math.sqrt(Math.pow(point.x - xx, 2) + Math.pow(point.y - yy, 2));
  const pointOnLine = { x: xx, y: yy };

  return { distance, pointOnLine, param };
};

/**
 * Znajduje najbliższą ścianę do danego punktu
 */
export const getClosestWall = (pos, walls, corners, threshold) => {
  let closestWall = null;
  let minDist = Infinity;
  let snapInfo = null;

  Object.values(walls).forEach(wall => {
    if (!wall.endCornerId) return; 

    const start = corners[wall.startCornerId];
    const end = corners[wall.endCornerId];
    if (!start || !end) return;

    const { distance: dist, pointOnLine, param } = pointToLineDistance(pos, start, end);

    if (dist <= threshold && dist < minDist && param >= 0 && param <= 1) {
      minDist = dist;
      closestWall = wall;
      snapInfo = {
        point: pointOnLine,
        param, 
        wallId: wall.id
      };
    }
  });

  return closestWall ? { wall: closestWall, snapInfo } : null;
};

/**
 * Konwertuje pozycję na ścianie na współrzędne world
 */
export const getPositionOnWall = (wall, corners, param) => {
  const start = corners[wall.startCornerId];
  const end = corners[wall.endCornerId];
  if (!start || !end) return null;

  return {
    x: start.x + (end.x - start.x) * param,
    y: start.y + (end.y - start.y) * param
  };
};

/**
 * Oblicza kąt ściany w radianach
 */
export const getWallAngle = (wall, corners) => {
  const start = corners[wall.startCornerId];
  const end = corners[wall.endCornerId];
  if (!start || !end) return 0;

  return Math.atan2(end.y - start.y, end.x - start.x);
};